import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../../apiClient';
import './ClubDiscovery.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const PACE_OPTIONS = ['Light', 'Moderate', 'Avid', 'Voracious'];
const BOOK_TYPE_OPTIONS = ['Fiction', 'Fact'];

function ClubDiscovery({ factFictionMode, onSelectClub, navigateTo }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [view, setView] = useState('discover');
  const [publicClubs, setPublicClubs] = useState([]);
  const [myClubs, setMyClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPublic: true,
    bookType: factFictionMode === 'fact' ? 'Fact' : 'Fiction',
    preferredPace: 'Moderate',
    maxMembers: 20,
  });

  const loadPublicClubs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const type = factFictionMode === 'fact' ? 'fact' : 'fiction';
      const data = await apiFetchJson(instance, `/api/bookclubs/discover?type=${type}&page=1&pageSize=20`);
      setPublicClubs(unwrap(data));
    } catch (e) {
      console.error('Failed to load public clubs:', e);
      setError('An error occurred while loading clubs.');
    } finally {
      setLoading(false);
    }
  }, [instance, factFictionMode]);

  const loadMyClubs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiFetchJson(instance, '/api/bookclubs');
      setMyClubs(unwrap(data));
    } catch (e) {
      console.error('Failed to load my clubs:', e);
      setError('An error occurred while loading your clubs.');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    if (view === 'discover') {
      loadPublicClubs();
    } else {
      loadMyClubs();
    }
  }, [view, loadPublicClubs, loadMyClubs]);

  const handleJoin = async (clubId) => {
    setJoining(clubId);
    try {
      await apiFetchJson(instance, `/api/bookclubs/${clubId}/join`, { method: 'POST' });
      await loadPublicClubs();
    } catch (e) {
      console.error('Failed to join club:', e);
      setError('An error occurred while joining.');
    } finally {
      setJoining(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    setCreating(true);
    try {
      await apiFetchJson(instance, '/api/bookclubs', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      setFormData({ name: '', description: '', isPublic: true, bookType: factFictionMode === 'fact' ? 'Fact' : 'Fiction', preferredPace: 'Moderate', maxMembers: 20 });
      setShowCreateForm(false);
      if (view === 'discover') {
        await loadPublicClubs();
      } else {
        await loadMyClubs();
      }
    } catch (e) {
      console.error('Failed to create club:', e);
      setError('An error occurred while creating the club.');
    } finally {
      setCreating(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const clubs = view === 'discover' ? publicClubs : myClubs;

  return (
    <div className="club-discovery">
      {/* View Toggle */}
      <div className="club-discovery-toggle">
        <button
          className={`club-discovery-toggle-btn ${view === 'discover' ? 'active' : ''}`}
          onClick={() => setView('discover')}
        >
          🌍 {t('bookClub.clubDiscovery.discoverPublic')}
        </button>
        <button
          className={`club-discovery-toggle-btn ${view === 'my-clubs' ? 'active' : ''}`}
          onClick={() => setView('my-clubs')}
        >
          👥 {t('bookClub.clubDiscovery.myClubs')}
        </button>
      </div>

      {/* Create Club Button */}
      <div className="club-discovery-actions">
        <button
          className="club-discovery-create-btn"
          onClick={() => setShowCreateForm(prev => !prev)}
        >
          {showCreateForm ? '✕ ' + t('bookClub.common.cancel') : '＋ ' + t('bookClub.clubDiscovery.createClub')}
        </button>
      </div>

      {/* Create Club Form */}
      {showCreateForm && (
        <form className="club-discovery-form" onSubmit={handleCreate}>
          <h3 className="club-discovery-form-title">{t('bookClub.clubDiscovery.createNewClub')}</h3>
          <div className="club-discovery-form-grid">
            <div className="club-discovery-form-field">
              <label className="club-discovery-label">{t('bookClub.clubDiscovery.clubName')}</label>
              <input
                className="club-discovery-input"
                type="text"
                placeholder={t('bookClub.clubDiscovery.clubNamePlaceholder')}
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
              />
            </div>
            <div className="club-discovery-form-field">
              <label className="club-discovery-label">{t('bookClub.clubDiscovery.description')}</label>
              <textarea
                className="club-discovery-textarea"
                placeholder={t('bookClub.clubDiscovery.descriptionPlaceholder')}
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                rows={3}
              />
            </div>
            <div className="club-discovery-form-row">
              <div className="club-discovery-form-field">
                <label className="club-discovery-label">{t('bookClub.clubDiscovery.bookType')}</label>
                <select
                  className="club-discovery-select"
                  value={formData.bookType}
                  onChange={(e) => handleFormChange('bookType', e.target.value)}
                >
                  {BOOK_TYPE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className="club-discovery-form-field">
                <label className="club-discovery-label">{t('bookClub.clubDiscovery.pace')}</label>
                <select
                  className="club-discovery-select"
                  value={formData.preferredPace}
                  onChange={(e) => handleFormChange('preferredPace', e.target.value)}
                >
                  {PACE_OPTIONS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="club-discovery-form-field">
                <label className="club-discovery-label">{t('bookClub.clubDiscovery.maxMembers')}</label>
                <input
                  className="club-discovery-input"
                  type="number"
                  min={2}
                  max={100}
                  value={formData.maxMembers}
                  onChange={(e) => handleFormChange('maxMembers', parseInt(e.target.value, 10) || 2)}
                />
              </div>
            </div>
            <div className="club-discovery-form-field club-discovery-form-toggle-field">
              <label className="club-discovery-label">{t('bookClub.clubDiscovery.publicClub')}</label>
              <button
                type="button"
                className={`club-discovery-toggle-switch ${formData.isPublic ? 'active' : ''}`}
                onClick={() => handleFormChange('isPublic', !formData.isPublic)}
              >
                <span className="club-discovery-toggle-knob" />
              </button>
            </div>
          </div>
          <button
            type="submit"
            className="club-discovery-submit-btn"
            disabled={creating || !formData.name.trim()}
          >
            {creating ? t('bookClub.clubDiscovery.creating') : '✓ ' + t('bookClub.clubDiscovery.createClubBtn')}
          </button>
        </form>
      )}

      {/* Error */}
      {error && (
        <div className="club-discovery-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="club-discovery-loading">
          <div className="club-discovery-spinner" />
          <p>{t('bookClub.clubDiscovery.loading')}</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && clubs.length === 0 && (
        <div className="club-discovery-empty">
          <span className="club-discovery-empty-icon">{view === 'discover' ? '🔍' : '📚'}</span>
          <p>{view === 'discover'
            ? t('bookClub.clubDiscovery.noPublicClubs')
            : t('bookClub.clubDiscovery.noMyClubs')
          }</p>
        </div>
      )}

      {/* Club Grid */}
      {!loading && clubs.length > 0 && (
        <div className="club-discovery-grid">
          {clubs.map(club => (
            <div
              key={club.id}
              className="club-discovery-card"
              onClick={() => onSelectClub(club.id)}
            >
              <div className="club-discovery-card-header">
                <h3 className="club-discovery-card-name">{club.name}</h3>
                <div className="club-discovery-card-badges">
                  <span className="club-discovery-badge club-discovery-badge-type">
                    {club.bookType === 'Fact' ? '📚 Fact' : '📖 Fiction'}
                  </span>
                  {club.preferredPace && (
                    <span className="club-discovery-badge club-discovery-badge-pace">
                      🕐 {club.preferredPace}
                    </span>
                  )}
                </div>
              </div>
              {club.description && (
                <p className="club-discovery-card-desc">{club.description}</p>
              )}
              <div className="club-discovery-card-footer">
                <span className="club-discovery-card-members">
                  👥 {club.memberCount}{club.maxMembers ? ` / ${club.maxMembers}` : ''}
                </span>
                {view === 'my-clubs' && club.activeRead && (
                  <span className="club-discovery-card-reading">
                    📖 {t('bookClub.clubDiscovery.reading')} {club.activeRead}
                  </span>
                )}
                {view === 'discover' && (
                  <button
                    className="club-discovery-join-btn"
                    onClick={(e) => { e.stopPropagation(); handleJoin(club.id); }}
                    disabled={joining === club.id}
                  >
                    {joining === club.id ? t('bookClub.clubDiscovery.joining') : t('bookClub.clubDiscovery.join')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ClubDiscovery;
